/**
 * Create Expense Invoice Page
 * Allows manual creation of expense invoices
 */

import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Alert } from '@mui/material';
import { ExpenseInvoiceForm } from '../Components/ExpenseInvoiceForm';
import SimplifiedExpenseInvoiceForm from '../Components/SimplifiedExpenseInvoiceForm';
import {
  useCreateExpenseInvoice,
  useCreateSimplifiedExpenseInvoice,
} from '../Service/extendedExpenseInvoicesApi';
import { ExpenseInvoicesRoutes } from '../../../Router/path';
import { useNotification } from '../../Invoices/Context/NotificationContext';
import type {
  CreateExpenseInvoiceDto,
} from '../Types/expenseInvoice.types';
import { useGetExpenseInvoiceCompaniesLookupQuery } from '../Service/expenseInvoiceCompaniesLookupApi';
import { useGetExpenseInvoiceTagsLookupQuery } from '../Service/expenseInvoiceTagsLookupApi';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import { isSimplifiedExpenseInvoiceDocument } from '../Utils/expenseInvoiceDocumentKind';
import type { SimplifiedExpenseInvoiceFormData } from '../Validation/simplifiedExpenseInvoiceValidation';
import { mapSimplifiedExpenseInvoiceFormDataToRequest } from '../Service/simplifiedExpenseInvoiceFormMapper';

export const CreateExpenseInvoicePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSuccess, showError } = useNotification();
  const [createExpenseInvoice, fullInvoiceMutationState] = useCreateExpenseInvoice();
  const [createSimplifiedExpenseInvoice, simplifiedMutationState] =
    useCreateSimplifiedExpenseInvoice();
  const isSimplifiedMode = isSimplifiedExpenseInvoiceDocument(searchParams.get('documentKind'));
  const {
    data: companies = [],
    isLoading: companiesLoading,
    isFetching: companiesFetching,
    isError: companiesLookupError,
    error: companiesError,
    refetch: refetchCompanies,
  } =
    useGetExpenseInvoiceCompaniesLookupQuery({ skip: !isSimplifiedMode });
  const {
    data: tags = [],
    isLoading: tagsLoading,
    isFetching: tagsFetching,
    isError: tagsLookupError,
    error: tagsError,
    refetch: refetchTags,
  } = useGetExpenseInvoiceTagsLookupQuery({
    skip: !isSimplifiedMode,
  });
  const simplifiedLookupError = companiesLookupError ? companiesError : tagsLookupError ? tagsError : null;
  const simplifiedLookupErrorMessage =
    isSimplifiedMode && simplifiedLookupError
      ? extractApiErrorMessage(
          simplifiedLookupError,
          'Nie udało się pobrać firm lub tagów dla kosztu uproszczonego.'
        )
      : null;

  const handleRetryLookups = () => {
    void Promise.all([refetchCompanies(), refetchTags()]);
  };

  const handleSubmit = async (data: CreateExpenseInvoiceDto) => {
    try {
      const result = await createExpenseInvoice(data).unwrap();

      showSuccess(`Faktura ${data.invoiceNumber} została utworzona`);
      navigate(ExpenseInvoicesRoutes.details.replace(':id', String(result.id)));
    } catch (err) {
      showError(
        extractApiErrorMessage(
          err,
          'Nie udało się utworzyć faktury kosztowej. Sprawdź formularz i spróbuj ponownie.'
        )
      );
    }
  };

  const handleSimplifiedSubmit = async (data: SimplifiedExpenseInvoiceFormData) => {
    try {
      const result = await createSimplifiedExpenseInvoice(
        mapSimplifiedExpenseInvoiceFormDataToRequest(data)
      ).unwrap();

      showSuccess(`Koszt uproszczony ${result.documentReference} został utworzony`);
      navigate(ExpenseInvoicesRoutes.details.replace(':id', String(result.id)));
    } catch (err) {
      showError(
        extractApiErrorMessage(
          err,
          'Nie udało się utworzyć kosztu uproszczonego. Sprawdź formularz i spróbuj ponownie.'
        )
      );
    }
  };

  const handleCancel = () => {
    navigate(ExpenseInvoicesRoutes.root);
  };

  return (
    <Box p={3}>
      {fullInvoiceMutationState.isError && !isSimplifiedMode && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {extractApiErrorMessage(
            fullInvoiceMutationState.error,
            'Nie udało się utworzyć faktury. Spróbuj ponownie.'
          )}
        </Alert>
      )}

      {simplifiedMutationState.isError && isSimplifiedMode && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {extractApiErrorMessage(
            simplifiedMutationState.error,
            'Nie udało się utworzyć kosztu uproszczonego. Spróbuj ponownie.'
          )}
        </Alert>
      )}

      {isSimplifiedMode ? (
        <SimplifiedExpenseInvoiceForm
          mode="create"
          companies={companies}
          tags={tags}
          companiesLoading={companiesLoading || companiesFetching}
          tagsLoading={tagsLoading || tagsFetching}
          lookupErrorMessage={simplifiedLookupErrorMessage}
          onRetryLookups={handleRetryLookups}
          onSubmit={handleSimplifiedSubmit}
          onCancel={handleCancel}
          isLoading={simplifiedMutationState.isLoading}
        />
      ) : (
        <ExpenseInvoiceForm
          mode="create"
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={fullInvoiceMutationState.isLoading}
        />
      )}
    </Box>
  );
};

export default CreateExpenseInvoicePage;
